import QRious from "qrious"

import { faCopy, faQrcode } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useState } from "react"
import styled, { css } from "styled-components"
import { WebLNProvider } from "webln"

import {
  BoldMonospace,
  Button,
  device,
  deviceSizes,
  GreenButton,
  HeaderText,
  ItalicText,
  Monospace,
  RegularText
} from "../styles"
import { SendPayment } from "../webln"
import { Card } from "./Card"
interface Props {
  invoice?: string
  webLN: WebLNProvider | undefined
}

const Container = styled(Card)`
  grid-area: invoice;
  display: flex;
  flex-direction: column;
`

const InnerContainer = styled.div<{ displayRow: boolean }>`
  display: flex
    ${({ displayRow }) =>
      displayRow
        ? css`
            flex-direction: row;
            align-items: center;
          `
        : css`
            flex-direction: column;
            align-items: flex-start;
          `};
`

const Img = styled.img`
  object-fit: contain;
`

const InvoiceText = styled.span`
  font-size: 70%;
`

const InvoiceAndLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const InvoiceContainer = styled(Monospace)`
  @media ${device.biggerThen.tabletS} {
    overflow-x: scroll;
    max-width: 30vw;
    text-overflow: ellipsis;
  }
  @media ${device.smallerThen.tabletS} {
    word-break: break-word;
    overflow-y: scroll;
    max-height: 70vw;
  }
`

const QRCodeContainer = styled(Container)`
  justify-content: space-evenly;
  align-items: center;

  flex-direction: row;

  @media ${device.biggerThen.tabletS} {
    padding: 0;
  }
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`

/** Tries to detect if the user is using ioS */
const isiOS = (): boolean => {
  const devices = [
    "iPad Simulator",
    "iPhone Simulator",
    "iPod Simulator",
    "iPad",
    "iPhone",
    "iPod"
  ]

  return devices.find(dev => dev === navigator.platform) !== undefined
}

/**
 * This component takes in an optional LN invoice,
 * and displays it.
 *
 * If it receives an invoice, it displays it as a clickable
 * `lightning:...` link that can be interpreted by the users
 * wallet.
 *
 * If it does not receive an invoice it displays text that
 * explains what's going on.
 */
export const InvoiceQr: React.SFC<Props> = ({ invoice, webLN }) => {
  const link = "lightning:" + invoice

  const [copyButtonText, setCopyButtonText] = useState("Copy")
  const [displayQR, setDisplayQR] = useState(false)

  const isMobile = window.innerWidth <= deviceSizes.mobileL

  if (displayQR && invoice) {
    const parentElement = document.getElementById("qr-invoice-container")!
    const qrBase64 = new QRious({
      value: invoice,
      // we want the QR code to almost fill the container here, but don't cause
      // any jitteryness
      size: isMobile
        ? parentElement.clientWidth * 0.6
        : Math.min(parentElement.clientHeight, parentElement.clientWidth) * 0.98
    }).toDataURL()

    return (
      <QRCodeContainer id="qr-invoice-container">
        <Img src={qrBase64} />
        <Button onClick={() => setDisplayQR(false)}>Close QR code</Button>
      </QRCodeContainer>
    )
  } else {
    return (
      <Container id="qr-invoice-container">
        {invoice !== undefined ? (
          <InnerContainer displayRow={invoice !== undefined}>
            <InvoiceAndLabelContainer>
              <BoldMonospace>Invoice:</BoldMonospace>
              <InvoiceContainer>
                <InvoiceText id="invoice-text">{invoice}</InvoiceText>
              </InvoiceContainer>
            </InvoiceAndLabelContainer>
            <ButtonContainer>
              <GreenButton
                onClick={() => {
                  if (webLN) {
                    SendPayment({ ln: webLN, invoice })
                  } else {
                    window.open(link)
                  }
                }}
              >
                Pay
              </GreenButton>
              {isiOS() ? null : ( // leave it out for now // how to copy/select text on iOS. Just // I cannot for the life of me figure out
                <Button
                  onClick={async () => {
                    // write invoice to clipboard
                    await navigator.clipboard.writeText(invoice)
                    // change button text
                    setCopyButtonText("Copied!")

                    // after 4 seconds, change text back to original
                    setTimeout(() => setCopyButtonText("Copy"), 4000)
                  }}
                >
                  <FontAwesomeIcon icon={faCopy} /> {copyButtonText}
                </Button>
              )}
              <Button onClick={() => setDisplayQR(true)}>
                <FontAwesomeIcon icon={faQrcode} /> QR
              </Button>
            </ButtonContainer>
          </InnerContainer>
        ) : (
          <>
            <ItalicText>No unpaid invoice received yet.</ItalicText>
            <RegularText>
              When you send a request that requires payment the invoice will be
              displayed here.
            </RegularText>
          </>
        )}
      </Container>
    )
  }
}
